import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToastFromSnackbar } from '@/utils/showToast';
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from '@/stores/userStore';
import { useLocales } from '@/locales';
import { useErrorDialogDispatch } from '@/stores/errorDialogStore';
import { edsService } from '@/pages/auth/services/edsService';
import { authService } from '@/pages/auth/services/authService';
import {
  EDS_TIMEOUTS,
  AUTH_ROUTES,
  USER_ROLES,
} from '@/pages/auth/constants/authConstants';
import { useAuthFlowStore } from '@/pages/auth/stores/authFlowStore';
import { validateEDSForm } from '@/pages/auth/utils/validation';

/**
 * Custom hook for EDS (Electronic Digital Signature) authentication
 */
export const useEDSAuth = () => {
  const navigate = useNavigate();

  const { translate } = useLocales();
  const setUser = useUserStore(state => state.setUser);
  const errorDispatch = useErrorDialogDispatch();

  // Auth flow store integration
  const {
    edsState,
    setEDSCertificates,
    setEDSSelectedIndex,
    setEDSSearchingKey,
    setEDSError,
    setEDSPrivacyAgreed,
    setEDSAuthenticating,
    setEDSKeyId,
    setEDSPkcsInfo,
    setEDSBackendGuid,
    resetEDSState,
    getCanSubmitEDS,
    addAuthAttempt,
  } = useAuthFlowStore();

  const {
    certificates,
    selectedIndex,
    selectedCert,
    isSearchingKey,
    isKeyFound,
    hasError,
    privacyAgreed,
    isAuthenticating,
    keyId,
    pkcsInfo,
    backendGuid,
  } = edsState;

  // Reset state
  const resetState = () => {
    resetEDSState();
  };

  // Search for EDS keys
  const handleStartSearchingKey = async () => {
    // Prevent multiple simultaneous searches
    if (isSearchingKey) {
      return;
    }

    setEDSError(false);
    setEDSSearchingKey(true);
    resetEDSState();

    try {
      // Add delay for UX and to prevent rate limiting
      await new Promise(resolve =>
        setTimeout(resolve, EDS_TIMEOUTS.SEARCH_KEY),
      );

      const result = await edsService.listAllUserKeys();

      if (result.success) {
        setEDSCertificates(result.data);
        setEDSSearchingKey(false);
      } else {
        throw result.error;
      }
    } catch (error) {
      setEDSSearchingKey(false);
      setEDSError(true);

      // Record failed attempt
      addAuthAttempt({
        type: 'eds',
        success: false,
        error: error.message || error.error?.message,
        stage: 'key_search',
      });

      // Show appropriate error message based on error type
      let errorMessage = translate(
        'The_system_could_not_find_the_EDS_key_on_your_computer',
      );

      if (error.type === 'connection_error') {
        errorMessage =
          error.error?.userMessage || error.error?.message || errorMessage;
      }

      showToastFromSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Select certificate
  const handleCertificateSelect = (certificate, index) => {
    setEDSSelectedIndex(index, certificate);
  };

  // Start authentication with selected key
  const handleAuthWithKey = async () => {
    // Prevent multiple simultaneous authentication attempts
    if (isAuthenticating) {
      return;
    }

    // Validate form
    const validation = validateEDSForm({
      selectedIndex,
      privacyAgreed,
      certificates,
    });

    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      return showToastFromSnackbar(firstError, { variant: 'warning' });
    }

    // Reset previous state to prevent conflicts
    setEDSKeyId(null);
    setEDSPkcsInfo('');
    setEDSBackendGuid(null);
    setEDSAuthenticating(true);

    try {
      // Get backend GUID
      const guidResult = await authService.getEDSGuid();
      setEDSBackendGuid(guidResult.guid);

      // Load key
      const keyResult = await edsService.loadKey(selectedCert);
      if (keyResult.success) {
        setEDSKeyId(keyResult.data);
      } else {
        throw keyResult.error;
      }
    } catch (error) {
      setEDSAuthenticating(false);

      // Record failed attempt
      addAuthAttempt({
        type: 'eds',
        success: false,
        error: error.message,
        stage: 'authentication',
      });

      errorDispatch({
        type: 'OPEN_ERROR_DIALOG',
        errorMsg: error.message || translate('IncorrectPassword'),
      });
    }
  };

  // Create PKCS7 signature when keyId and backendGuid are available
  useEffect(() => {
    const createSignature = async () => {
      if (!keyId || !backendGuid || !isAuthenticating) return;

      try {
        const pkcsResult = await edsService.createPkcs7(keyId.id, backendGuid);
        if (pkcsResult.success) {
          setEDSPkcsInfo(pkcsResult.data);
        } else {
          throw pkcsResult.error;
        }
      } catch (error) {
        setEDSAuthenticating(false);

        // Record failed attempt
        addAuthAttempt({
          type: 'eds',
          success: false,
          error: error.message,
          stage: 'signature_creation',
        });

        errorDispatch({
          type: 'OPEN_ERROR_DIALOG',
          errorMsg: error.message || translate('systemError'),
        });
      }
    };

    createSignature();
  }, [keyId, backendGuid, isAuthenticating, errorDispatch, translate]);

  // Verify signature and complete authentication
  useEffect(() => {
    const verifyAndComplete = async () => {
      if (!pkcsInfo || !keyId || !isAuthenticating) return;

      try {
        const result = await authService.verifyEDSSignature({
          pkcs: pkcsInfo,
          infoCert: keyId,
        });

        if (result.message === 'success') {
          // Record successful attempt
          addAuthAttempt({
            type: 'eds',
            success: true,
            userId: result.user._id,
          });

          const user = {
            id: result.user._id,
            cert: selectedIndex,
            keyId: keyId,
            role: USER_ROLES.USER,
            isVerified: false,
            token: result.token,
            details: result.user,
            socketUUID: uuidv4(),
          };

          setUser(user);

          showToastFromSnackbar(translate('Login_Successful'), {
            variant: 'success',
          });

          // Reset authentication state before navigation
          setEDSAuthenticating(false);

          setTimeout(() => {
            navigate(AUTH_ROUTES.USER_SUBMISSIONS);
          }, EDS_TIMEOUTS.AUTH_DELAY);
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        setEDSAuthenticating(false);

        // Record failed attempt
        addAuthAttempt({
          type: 'eds',
          success: false,
          error: error.message,
          stage: 'verification',
        });

        errorDispatch({
          type: 'OPEN_ERROR_DIALOG',
          errorMsg: error.message || translate('errorOutGoing'),
        });
      }
    };

    verifyAndComplete();
  }, [
    pkcsInfo,
    keyId,
    isAuthenticating,
    selectedIndex,
    setUser,
    navigate,
    translate,
    errorDispatch,
    addAuthAttempt,
  ]);

  return {
    // State
    certificates,
    selectedIndex,
    selectedCert,
    isSearchingKey,
    isKeyFound,
    hasError,
    privacyAgreed,
    isAuthenticating,
    canSubmit: getCanSubmitEDS(),

    // Actions
    handleStartSearchingKey,
    handleCertificateSelect,
    handleAuthWithKey,
    setPrivacyAgreed: setEDSPrivacyAgreed,
    resetState,
  };
};
